import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { MetricsFilters } from "@/components/metrics/MetricsFilters";
import { ClientGrowthChart } from "@/components/metrics/ClientGrowthChart";
import { RevenueChart } from "@/components/metrics/RevenueChart";
import { SubscriptionStatusChart } from "@/components/metrics/SubscriptionStatusChart";
import { PlanDistributionChart } from "@/components/metrics/PlanDistributionChart";
import { ServiceRevenueChart } from "@/components/metrics/ServiceRevenueChart";
import { metricsService, MetricFilters } from "@/services/metrics";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  TrendingUp,
  DollarSign,
  Activity,
  PieChart,
  BarChart2,
} from "lucide-react";
import { useUrlFilters } from "@/hooks/use-url-filters";

const DEFAULT_FILTERS = {
  from: "",
  to: "",
  period: "",
};

function toMetricFilters(values: typeof DEFAULT_FILTERS): MetricFilters {
  return {
    from: values.from || undefined,
    to: values.to || undefined,
    period: (values.period || undefined) as MetricFilters["period"],
  };
}

export default function Metrics() {
  const [urlFilters, setUrlFilters] = useUrlFilters(DEFAULT_FILTERS);
  const appliedFilters = toMetricFilters(urlFilters);

  const [pendingFilters, setPendingFilters] = useState<MetricFilters>(appliedFilters);

  // Si la URL cambia externamente (Atrás/Adelante), refleja lo aplicado en el formulario
  useEffect(() => {
    setPendingFilters(appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFilters.from, urlFilters.to, urlFilters.period]);

  const handleApply = () => {
    setUrlFilters({
      from: pendingFilters.from ?? "",
      to: pendingFilters.to ?? "",
      period: pendingFilters.period ?? "",
    });
  };

  const queryOpts = { filters: appliedFilters };

  const { data: clientGrowthRes, isLoading: isLoadingCG } = useQuery({
    queryKey: ["metrics", "client-growth", queryOpts],
    queryFn: () => metricsService.getClientGrowth(appliedFilters),
  });

  const { data: revenueRes, isLoading: isLoadingRev } = useQuery({
    queryKey: ["metrics", "revenue", queryOpts],
    queryFn: () => metricsService.getRevenue(appliedFilters),
  });

  const { data: statusRes, isLoading: isLoadingStatus } = useQuery({
    queryKey: ["metrics", "subscription-status", queryOpts],
    queryFn: () => metricsService.getSubscriptionStatus(appliedFilters),
  });

  const { data: planRes, isLoading: isLoadingPlan } = useQuery({
    queryKey: ["metrics", "plan-distribution", queryOpts],
    queryFn: () => metricsService.getPlanDistribution(appliedFilters),
  });

  const { data: serviceRevRes, isLoading: isLoadingServiceRev } = useQuery({
    queryKey: ["metrics", "service-revenue", queryOpts],
    queryFn: () => metricsService.getServiceRevenue(appliedFilters),
  });

  return (
    <DashboardLayout title="Métricas" subtitle="Análisis de rendimiento del negocio">
      <div className="space-y-6 animate-fade-in">

        {/* Filters */}
        <MetricsFilters
          filters={pendingFilters}
          onChange={setPendingFilters}
          onApply={handleApply}
        />

        {/* Row 1 — Client Growth (full width) */}
        <Card className="shadow-sm">
          <CardHeader className="pb-2 flex flex-row items-center gap-2">
            <div className="p-1.5 rounded-lg bg-primary/10">
              <TrendingUp className="h-4 w-4 text-primary" />
            </div>
            <CardTitle className="text-base font-semibold">Crecimiento de Clientes</CardTitle>
          </CardHeader>
          <CardContent>
            <ClientGrowthChart data={clientGrowthRes?.data || []} isLoading={isLoadingCG} />
          </CardContent>
        </Card>

        {/* Row 2 — Revenue (2/3) + Subscription Status (1/3) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 shadow-sm">
            <CardHeader className="pb-2 flex flex-row items-center gap-2">
              <div className="p-1.5 rounded-lg bg-success/10">
                <DollarSign className="h-4 w-4 text-success" />
              </div>
              <CardTitle className="text-base font-semibold">Ingresos</CardTitle>
            </CardHeader>
            <CardContent>
              <RevenueChart data={revenueRes?.data || []} isLoading={isLoadingRev} />
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="pb-2 flex flex-row items-center gap-2">
              <div className="p-1.5 rounded-lg bg-warning/10">
                <Activity className="h-4 w-4 text-warning" />
              </div>
              <CardTitle className="text-base font-semibold">Estado Suscripciones</CardTitle>
            </CardHeader>
            <CardContent>
              <SubscriptionStatusChart data={statusRes?.data || []} isLoading={isLoadingStatus} />
            </CardContent>
          </Card>
        </div>

        {/* Row 3 — Service Revenue (1/2) + Plan Distribution (1/2) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="shadow-sm">
            <CardHeader className="pb-2 flex flex-row items-center gap-2">
              <div className="p-1.5 rounded-lg bg-[#8B5CF6]/10">
                <BarChart2 className="h-4 w-4 text-[#8B5CF6]" />
              </div>
              <CardTitle className="text-base font-semibold">Ingresos por Servicio</CardTitle>
            </CardHeader>
            <CardContent>
              <ServiceRevenueChart data={serviceRevRes?.data || []} isLoading={isLoadingServiceRev} />
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardHeader className="pb-2 flex flex-row items-center gap-2">
              <div className="p-1.5 rounded-lg bg-[#06B6D4]/10">
                <PieChart className="h-4 w-4 text-[#06B6D4]" />
              </div>
              <CardTitle className="text-base font-semibold">Distribución de Planes</CardTitle>
            </CardHeader>
            <CardContent>
              <PlanDistributionChart data={planRes?.data || []} isLoading={isLoadingPlan} />
            </CardContent>
          </Card>
        </div>

      </div>
    </DashboardLayout>
  );
}
